"use client"

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react"

/**
 * Move the item at `from` so it lands at visual index `hoverIdx` in the final list.
 */
export function moveItemInList<T>(items: T[], from: number, hoverIdx: number): T[] {
  const n = items.length
  if (from < 0 || from >= n) return items
  if (hoverIdx < 0 || hoverIdx > n) return items
  if (from === hoverIdx) return items
  const copy = [...items]
  const [removed] = copy.splice(from, 1)
  let target = hoverIdx
  if (from < hoverIdx) target = hoverIdx - 1
  if (hoverIdx === n) {
    copy.push(removed)
    return copy
  }
  copy.splice(target, 0, removed)
  return copy
}

function computePointerInsertSlot<T extends { id: string }>(
  clientY: number,
  visualOrder: T[],
  getRowEl: (id: string) => HTMLElement | undefined,
): number {
  const n = visualOrder.length
  if (n === 0) return 0
  for (let i = 0; i < n; i++) {
    const el = getRowEl(visualOrder[i].id)
    if (!el) continue
    const r = el.getBoundingClientRect()
    const last = i === n - 1
    if (last && clientY >= r.top + r.height / 2) return n
    if (clientY < r.top + r.height / 2) return i
  }
  return n
}

function composedOrderFromPreviewSlot<T extends { id: string }>(
  preview: T[],
  dragId: string,
  slot: number,
): T[] {
  const dragEntry = preview.find((p) => p.id === dragId)
  if (!dragEntry) return preview
  const without = preview.filter((p) => p.id !== dragId)
  const s = Math.max(0, Math.min(slot, without.length))
  return [...without.slice(0, s), dragEntry, ...without.slice(s)]
}

function hoverIndexForComposedOrder<T extends { id: string }>(
  items: T[],
  fromIdx: number,
  composed: T[],
): number {
  const target = composed.map((p) => p.id).join("\0")
  const n = items.length
  for (let h = 0; h <= n; h++) {
    if (moveItemInList(items, fromIdx, h).map((p) => p.id).join("\0") === target) return h
  }
  return fromIdx
}

type ListDragSession = {
  id: string
  width: number
  height: number
  grabOffsetX: number
  grabOffsetY: number
  x: number
  y: number
  overIndex: number
}

/** Pointer reorder: live preview order, placeholder, floater (same behavior as contact phone/email rows). */
export function usePointerListReorder<T extends { id: string }>(
  items: T[],
  onReorder: (next: T[]) => void,
) {
  const [drag, setDrag] = useState<ListDragSession | null>(null)
  const dragRef = useRef(drag)
  const rowElRefs = useRef<Map<string, HTMLElement>>(new Map())
  const itemsRef = useRef(items)
  const onReorderRef = useRef(onReorder)

  useLayoutEffect(() => {
    dragRef.current = drag
  }, [drag])
  useLayoutEffect(() => {
    itemsRef.current = items
  }, [items])
  useLayoutEffect(() => {
    onReorderRef.current = onReorder
  }, [onReorder])

  const registerRowRef = useCallback((id: string) => (el: HTMLElement | null) => {
    if (el) rowElRefs.current.set(id, el)
    else rowElRefs.current.delete(id)
  }, [])

  const onGripPointerDown = useCallback(
    (e: ReactPointerEvent, item: T, rowSelector: string) => {
      if (e.button !== 0) return
      e.preventDefault()
      const row = (e.currentTarget as HTMLElement).closest(rowSelector) as HTMLElement | null
      if (!row) return
      const rect = row.getBoundingClientRect()
      const fromIndex = items.findIndex((x) => x.id === item.id)
      if (fromIndex < 0) return
      setDrag({
        id: item.id,
        width: rect.width,
        height: rect.height,
        grabOffsetX: e.clientX - rect.left,
        grabOffsetY: e.clientY - rect.top,
        x: rect.left,
        y: rect.top,
        overIndex: fromIndex,
      })
    },
    [items],
  )

  useEffect(() => {
    if (!drag) return

    const onMove = (ev: globalThis.PointerEvent) => {
      setDrag((prev) => {
        if (!prev) return null
        const list = itemsRef.current
        const fromIdx = list.findIndex((x) => x.id === prev.id)
        if (fromIdx === -1) {
          return {
            ...prev,
            x: ev.clientX - prev.grabOffsetX,
            y: ev.clientY - prev.grabOffsetY,
          }
        }
        const preview = moveItemInList(list, fromIdx, prev.overIndex)
        const slot = computePointerInsertSlot(ev.clientY, preview, (id) => rowElRefs.current.get(id))
        const composed = composedOrderFromPreviewSlot(preview, prev.id, slot)
        const overIndex = hoverIndexForComposedOrder(list, fromIdx, composed)
        return {
          ...prev,
          x: ev.clientX - prev.grabOffsetX,
          y: ev.clientY - prev.grabOffsetY,
          overIndex,
        }
      })
    }

    const endDrag = () => {
      const prev = dragRef.current
      if (prev) {
        const list = itemsRef.current
        const fromIdx = list.findIndex((x) => x.id === prev.id)
        if (fromIdx !== -1) {
          onReorderRef.current(moveItemInList(list, fromIdx, prev.overIndex))
        }
      }
      setDrag(null)
    }

    document.addEventListener("pointermove", onMove)
    document.addEventListener("pointerup", endDrag)
    document.addEventListener("pointercancel", endDrag)
    return () => {
      document.removeEventListener("pointermove", onMove)
      document.removeEventListener("pointerup", endDrag)
      document.removeEventListener("pointercancel", endDrag)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag?.id])

  useEffect(() => {
    if (!drag) return
    const prevUserSelect = document.body.style.userSelect
    const prevCursor = document.body.style.cursor
    document.body.style.userSelect = "none"
    document.body.style.cursor = "grabbing"
    return () => {
      document.body.style.userSelect = prevUserSelect
      document.body.style.cursor = prevCursor
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag?.id])

  useEffect(() => {
    if (!drag) return
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setDrag(null)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag?.id])

  const fromIdx = drag ? items.findIndex((x) => x.id === drag.id) : -1
  const displayList =
    drag && fromIdx >= 0 ? moveItemInList(items, fromIdx, drag.overIndex) : items
  const dragFloater = drag ? items.find((x) => x.id === drag.id) : null

  return { drag, registerRowRef, onGripPointerDown, displayList, dragFloater }
}
